import { useState } from 'react'
import { useSearchParams } from 'react-router'
import omitBy from 'lodash-es/omitBy'

import { EMPTY_OBJECT } from '../../../constants/common'
import {
  PAGINATION_DEFAULT_COUNT_VALUE,
  PAGINATION_PAGE_PARAM,
  PAGINATION_PAGE_SIZES,
} from '../../../constants/pagination'
import { PAGE_DEFAULT_VALUE } from '../../../constants/route'
import {
  getPageFromSearchParams,
  isEqualPagination,
} from '../../../utils/pagination'

export function useLocationPagination({
  defaultPage = PAGE_DEFAULT_VALUE,
  isUrlBound = true,
} = EMPTY_OBJECT) {
  const [searchParams, setSearchParams] = useSearchParams()

  const page = isUrlBound ? getPageFromSearchParams(searchParams) : defaultPage

  const [value, setValue] = useState(() => ({
    count: PAGINATION_DEFAULT_COUNT_VALUE,
    page,
    pageCount: PAGINATION_DEFAULT_COUNT_VALUE,
    pageSize: PAGINATION_PAGE_SIZES[0],
  }))
  const [prevPage, setPrevPage] = useState(page)

  // Mirror the URL page into local state — during render rather than in an
  // effect.
  if (isUrlBound && page !== prevPage) {
    setPrevPage(page)
    setValue(prevValue =>
      prevValue.page === page ? prevValue : { ...prevValue, page }
    )
  }

  function setPagination(nextValue, setSearchParamsOptions = EMPTY_OBJECT) {
    let nextParams = null

    setValue(currValue =>
      isEqualPagination(currValue, nextValue) ? currValue : nextValue
    )

    if (isUrlBound && page !== nextValue.page) {
      nextParams = {
        [PAGINATION_PAGE_PARAM]: nextValue.page,
      }
    }

    if (nextParams != null) {
      setSearchParams(
        omitBy(
          {
            ...Object.fromEntries(searchParams.entries()),
            ...nextParams,
          },
          v => !v
        ),
        setSearchParamsOptions
      )
    }
  }

  function setPageSize(pageSize) {
    setValue(prevValue =>
      prevValue.pageSize === pageSize
        ? prevValue
        : { ...prevValue, pageSize: pageSize }
    )
  }

  return { pagination: value, setPageSize, setPagination }
}
